
import { useState, useRef, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import Card from '../../components/base/Card';

// Turnstile CAPTCHA type declaration
declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: {
        sitekey: string;
        theme?: 'light' | 'dark' | 'auto';
        callback?: (token: string) => void;
        'expired-callback'?: () => void;
        'error-callback'?: () => void;
      }) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
    };
  }
}

// Turnstile Site Key
const TURNSTILE_SITE_KEY = '0x4AAAAAACHI6UJ5Gjz87OBO';

export default function Contact() {
  const [activeForm, setActiveForm] = useState<string | null>(null);
  const formSectionRef = useRef<HTMLDivElement>(null);
  
  // Add scroll effect when form is activated
  useEffect(() => {
    if (activeForm && formSectionRef.current) {
      formSectionRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [activeForm]);

  const handleFormActivation = (formType: string) => {
    setActiveForm(formType);
  };

  const [formData, setFormData] = useState({
    serviceRequest: {
      name: '',
      email: '',
      phone: '',
      company: '',
      industry: '',
      serviceType: '',
      equipment: '',
      message: ''
    },
    consultation: {
      name: '',
      email: '',
      phone: '',
      company: '',
      industry: '',
      equipmentType: '',
      capacity: '',
      budget: '',
      timeline: '',
      requirements: ''
    },
    catalog: {
      name: '',
      email: '',
      phone: '',
      company: '',
      position: '',
      industry: '',
      catalogType: ''
    },
    contactUs: {
      name: '',
      email: '',
      phone: '',
      company: '',
      subject: '',
      message: ''
    }
  });
  const [submitStatus, setSubmitStatus] = useState<{[key: string]: string}>({});

  const handleInputChange = (formType: keyof typeof formData, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [formType]: {
        ...prev[formType],
        [field]: value
      }
    }));
  };

  // Form endpoint mapping
  const formEndpoints: Record<string, string> = {
    serviceRequest: '/api/service.php',
    consultation: '/api/consultation.php',
    catalog: '/api/catalog.php',
    contactUs: '/api/contact.php'
  };

  const handleSubmit = async (formType: 'serviceRequest' | 'consultation' | 'catalog' | 'contactUs') => {
    const data = formData[formType];

    // Get Turnstile token
    const turnstileToken = window.turnstile?.getResponse?.();
    if (!turnstileToken) {
      setSubmitStatus({...submitStatus, [formType]: 'Please complete the CAPTCHA verification'});
      return;
    }

    // Validate required fields
    const requiredFields = formType === 'serviceRequest'
      ? ['name', 'email', 'phone', 'serviceType']
      : formType === 'consultation'
      ? ['name', 'email', 'phone', 'equipmentType']
      : formType === 'catalog'
      ? ['name', 'email', 'phone', 'catalogType']
      : ['name', 'email', 'phone', 'subject', 'message'];

    const missingFields = requiredFields.filter(field => !data[field as keyof typeof data]);
    if (missingFields.length > 0) {
      setSubmitStatus({...submitStatus, [formType]: 'Please fill in all required fields'});
      return;
    }

    try {
      setSubmitStatus({...submitStatus, [formType]: 'Submitting...'});

      // Prepare JSON payload with Turnstile token
      const payload = {
        ...data,
        turnstileToken
      };

      const response = await fetch(formEndpoints[formType], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setSubmitStatus({...submitStatus, [formType]: result.message || 'Request submitted successfully! We will contact you soon.'});
        // Reset form
        setFormData(prev => ({
          ...prev,
          [formType]: Object.keys(prev[formType]).reduce((acc, key) => ({...acc, [key]: ''}), {} as typeof prev[typeof formType])
        }));
        // Reset Turnstile
        window.turnstile?.reset?.();
        if (formType !== 'contactUs') {
          setTimeout(() => setActiveForm(null), 3000);
        }
      } else {
        setSubmitStatus({...submitStatus, [formType]: result.error || 'Submission failed. Please try again.'});
        window.turnstile?.reset?.();
      }
    } catch (error) {
      setSubmitStatus({...submitStatus, [formType]: 'Network error. Please try again.'});
      window.turnstile?.reset?.();
    }
  };

  const contactInfo = [
    {
      icon: 'ri-map-pin-line',
      title: 'Head Office',
      details: ['Sy No. 53/4, Site No. 5, P & T Layout Main Road', 'Ashwath Nagar, Srigadhakavalu', 'Sunkadakatte, Bangalore - 560091'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'ri-phone-line',
      title: 'Phone Numbers',
      details: ['+91 99808 72453'],
      color: 'from-teal-500 to-teal-600'
    },
    {
      icon: 'ri-mail-line',
      title: 'Email Addresses',
      details: ['info@galaxyfabtech.com', 'sales@galaxyfabtech.com', 'www.galaxyfabtech.com'],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: 'ri-time-line',
      title: 'Business Hours',
      details: ['Monday - Friday: 9:00 AM - 6:00 PM', 'Saturday: 9:00 AM - 2:00 PM', 'Sunday: Closed'],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const serviceTypes = [
    'Equipment Installation',
    'Maintenance & Repair',
    'Spare Parts Supply',
    'Technical Training',
    'Equipment Consultation',
    'Project Planning'
  ];

  const industries = [
    'Hospitality',
    'Healthcare',
    'Educational',
    'Laundromats',
    'Industrial',
    'Government'
  ];

  const equipmentTypes = [
    'Washer Extractors',
    'Tumble Dryers',
    'Flat Work Ironers',
    'Vacuum Iron Tables',
    'Finishing Extractors',
    'Stack Systems'
  ];

  const catalogTypes = [
    'Complete Product Catalog',
    'Washer Extractors Brochure',
    'Tumble Dryers Brochure',
    'Flat Work Ironers Brochure',
    'Service Manual',
    'Technical Specifications'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section - Professional Glass-Effect Design */}
      <section className="pt-32 pb-20 bg-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center opacity-5"
            style={{
              backgroundImage: `url('/images/backgrounds/contact-hero-bg.jpg')`
            }}
          ></div>
        </div>

        {/* Floating Background Elements */}
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/30 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-teal-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-purple-200/30 rounded-full blur-xl animate-pulse delay-500"></div>
        <div className="absolute top-1/4 right-1/3 w-36 h-36 bg-cyan-200/30 rounded-full blur-2xl animate-pulse delay-1500"></div>

        {/* Floating 3D Icon Elements */}
        <div className="absolute top-40 left-1/3 w-12 h-12 bg-white/90 backdrop-blur-2xl border border-white/30 rounded-2xl flex items-center justify-center shadow-2xl animate-float">
          <i className="ri-customer-service-2-line text-blue-500 text-lg"></i>
        </div>
        <div className="absolute bottom-32 right-1/3 w-12 h-12 bg-white/90 backdrop-blur-2xl border border-white/30 rounded-2xl flex items-center justify-center shadow-2xl animate-float" style={{ animationDelay: '1.5s' }}>
          <i className="ri-phone-line text-teal-500 text-lg"></i>
        </div>
        <div className="absolute top-1/3 left-20 w-10 h-10 bg-white/80 backdrop-blur-xl border border-white/40 rounded-xl flex items-center justify-center shadow-xl animate-float" style={{ animationDelay: '0.8s' }}>
          <i className="ri-mail-line text-purple-500 text-sm"></i>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="text-center">
            <div className="mb-8">
              <span className="inline-flex items-center px-6 py-3 rounded-full bg-white/60 backdrop-blur-2xl border border-white/30 text-blue-800 text-sm font-semibold shadow-2xl">
                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-customer-service-2-line text-sm text-blue-600"></i>
                </div>
                Expert Support & Consultation
                <span className="w-2 h-2 bg-blue-500 rounded-full ml-3 animate-pulse"></span>
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight">
              Get In Touch With{' '}
              <span className="text-blue-600">
                Our Experts
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              Contact our professional team for expert consultation, service requests, or to download comprehensive equipment catalogs
            </p>
          </div>
        </div>

        {/* Custom CSS for floating animation */}
        <style jsx>{`
          @keyframes animate-float {
            0%, 100% {
              transform: translateY(0px) rotate(0deg);
            }
            50% {
              transform: translateY(-10px) rotate(5deg);
            }
          }
          .animate-float {
            animation: animate-float 3s ease-in-out infinite;
          }
        `}</style>
      </section>

      {/* Action Buttons - Enhanced Glass Design */}
      <section className="py-8 bg-white/50 backdrop-blur-sm shadow-2xl border-y border-white/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Buttons removed as requested */}
        </div>
      </section>

      {/* Contact Information Grid - Professional Glass Containers */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-40 h-40 bg-blue-200/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-teal-200/25 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-28 h-28 bg-purple-200/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/60 backdrop-blur-2xl border border-white/30 text-blue-800 text-sm font-semibold shadow-2xl mb-6">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                <i className="ri-contacts-book-line text-blue-600 text-sm"></i>
              </div>
              Multiple Contact Options
              <div className="w-2 h-2 bg-blue-500 rounded-full ml-3 animate-pulse"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get In Touch With Us
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Multiple ways to reach our expert team for all your commercial laundry equipment needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="group cursor-pointer">
                <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 group-hover:scale-105 border border-white/30 relative overflow-hidden h-full">
                  {/* Glass Background Layers */}
                  <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
                  
                  {/* Floating Particles */}
                  <div className="absolute top-4 right-4 w-1 h-1 bg-blue-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                  <div className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-blue-400/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.3s' }}></div>

                  <div className="relative z-10 text-center">
                    {/* 3D Icon Container */}
                    <div className="relative mb-6">
                      <div className={`w-20 h-20 bg-gradient-to-br ${info.color} rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 transition-all duration-500 border border-white/30 relative`}>
                        {/* 3D Shadow Effect */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${info.color.replace('500', '600').replace('600', '700')} rounded-3xl transform translate-x-1 translate-y-1 -z-10 group-hover:translate-x-2 group-hover:translate-y-2 transition-transform duration-500`}></div>
                        <i className={`${info.icon} text-white text-3xl group-hover:scale-110 transition-transform duration-300`}></i>

                        {/* Icon Glow Effect */}
                        <div className={`absolute inset-0 bg-gradient-to-br ${info.color.replace('500', '400').replace('600', '400')}/50 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500`}></div>
                      </div>

                      {/* Floating Ring */}
                      <div className="absolute inset-0 w-20 h-20 mx-auto border-2 border-blue-300/30 rounded-3xl group-hover:scale-150 group-hover:border-blue-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                    </div>

                    {/* Content Container with Glass Effect */}
                    <div className="relative bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 group-hover:bg-white/30 transition-all duration-500">
                      <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-gray-800 transition-colors duration-300">
                        {info.title}
                      </h3>
                      
                      {/* Decorative Line */}
                      <div className="w-12 h-1 bg-blue-500 rounded-full mx-auto mb-6 group-hover:w-20 transition-all duration-500 shadow-lg"></div>
                      
                      <div className="space-y-2">
                        {info.details.map((detail, idx) => (
                          <p key={idx} className="text-gray-700 text-sm leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                            {detail}
                          </p>
                        ))}
                      </div>

                      {/* Interactive Progress Bar */}
                      <div className="mt-6 h-2 bg-white/30 rounded-full overflow-hidden">
                        <div className="h-full bg-blue-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                      </div>
                    </div>

                    {/* Hover Arrow Indicator */}
                    <div className="absolute bottom-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500 border border-white/40">
                      <i className="ri-arrow-up-right-line text-blue-600 text-lg group-hover:scale-110 transition-transform duration-300"></i>
                    </div>
                  </div>

                  {/* Subtle Shine Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-3xl"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service Request Forms - Enhanced Glass Design */}
      <section className="py-20 bg-white/50 backdrop-blur-sm relative overflow-hidden" ref={formSectionRef}>
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/20 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-28 h-28 bg-teal-200/25 rounded-100 blur-xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/60 backdrop-blur-2xl border border-white/30 text-blue-800 text-sm font-semibold shadow-2xl mb-6">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                <i className="ri-questionnaire-line text-blue-600 text-sm"></i>
              </div>
              Professional Service Forms
              <div className="w-2 h-2 bg-blue-500 rounded-full ml-3 animate-pulse"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How Can We Help You?
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Choose the appropriate form below to get started with your laundry equipment requirements
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            <div className="group cursor-pointer" onClick={() => handleFormActivation('serviceRequest')}>
              <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 group-hover:scale-105 border border-white/30 relative overflow-hidden h-full">
                {/* Glass Background Layers */}
                <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-blue-500/5 rounded-3xl"></div>
                
                {/* Floating Particles */}
                <div className="absolute top-4 right-4 w-1 h-1 bg-blue-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                <div className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-blue-400/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.3s' }}></div>

                <div className="relative z-10 text-center">
                  {/* 3D Icon Container */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 transition-all duration-500 border border-white/30 relative">
                      {/* 3D Shadow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                      <i className="ri-tools-line text-white text-4xl group-hover:scale-110 transition-transform duration-300"></i>

                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/50 to-blue-400/30 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500"></div>
                    </div>

                    {/* Floating Ring */}
                    <div className="absolute inset-0 w-24 h-24 mx-auto border-2 border-blue-300/30 rounded-3xl group-hover:scale-150 group-hover:border-blue-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                  </div>

                  {/* Content Container */}
                  <div className="relative bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 group-hover:bg-white/30 transition-all duration-500">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-blue-700 transition-colors duration-300">
                      Service Request
                    </h3>
                    
                    <div className="w-12 h-1 bg-blue-500 rounded-full mx-auto mb-6 group-hover:w-20 transition-all duration-500 shadow-lg"></div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      Need installation, maintenance, or repair services for your existing equipment?
                    </p>
                    
                    <div className="inline-flex items-center px-4 py-2 bg-white/30 backdrop-blur-xl border border-white/40 rounded-xl text-blue-600 font-medium group-hover:bg-white/50 transition-all duration-300">
                      Request Service
                    </div>

                    {/* Interactive Progress Bar */}
                    <div className="mt-6 h-2 bg-white/30 rounded-full overflow-hidden">
                      <div className="h-full bg-blue-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer" onClick={() => handleFormActivation('consultation')}>
              <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 group-hover:scale-105 border border-white/30 relative overflow-hidden h-full">
                {/* Glass Background Layers */}
                <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 via-transparent to-teal-500/5 rounded-3xl"></div>
                
                {/* Floating Particles */}
                <div className="absolute top-4 right-4 w-1 h-1 bg-teal-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                <div className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-teal-400/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.3s' }}></div>

                <div className="relative z-10 text-center">
                  {/* 3D Icon Container */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 transition-all duration-500 border border-white/30 relative">
                      {/* 3D Shadow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-teal-600 to-teal-700 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                      <i className="ri-calculator-line text-white text-4xl group-hover:scale-110 transition-transform duration-300"></i>

                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-teal-400/50 to-teal-400/30 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500"></div>
                    </div>

                    {/* Floating Ring */}
                    <div className="absolute inset-0 w-24 h-24 mx-auto border-2 border-teal-300/30 rounded-3xl group-hover:scale-150 group-hover:border-teal-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                  </div>

                  {/* Content Container */}
                  <div className="relative bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 group-hover:bg-white/30 transition-all duration-500">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-teal-700 transition-colors duration-300">
                      Get a Quote
                    </h3>
                    
                    <div className="w-12 h-1 bg-teal-500 rounded-full mx-auto mb-6 group-hover:w-20 transition-all duration-500 shadow-lg"></div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      Request a detailed quote for your laundry equipment and installation requirements.
                    </p>
                    
                    <div className="inline-flex items-center px-4 py-2 bg-white/30 backdrop-blur-xl border border-white/40 rounded-xl text-teal-600 font-medium group-hover:bg-white/50 transition-all duration-300">
                      Get Quote
                    </div>

                    {/* Interactive Progress Bar */}
                    <div className="mt-6 h-2 bg-white/30 rounded-full overflow-hidden">
                      <div className="h-full bg-teal-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="group cursor-pointer" onClick={() => handleFormActivation('catalog')}>
              <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:-translate-y-3 group-hover:scale-105 border border-white/30 relative overflow-hidden h-full">
                {/* Glass Background Layers */}
                <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-purple-5 0/5 rounded-3xl"></div>
                
                {/* Floating Particles */}
                <div className="absolute top-4 right-4 w-1 h-1 bg-purple-400/60 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-ping transition-all duration-500"></div>
                <div className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-purple-400/50 rounded-full opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-all duration-700" style={{ animationDelay: '0.3s' }}></div>

                <div className="relative z-10 text-center">
                  {/* 3D Icon Container */}
                  <div className="relative mb-6">
                    <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto shadow-2xl group-hover:shadow-3xl group-hover:scale-110 transition-all duration-500 border border-white/30 relative">
                      {/* 3D Shadow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-purple-700 rounded-3xl transform translate-x-2 translate-y-2 -z-10 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-500"></div>
                      <i className="ri-download-line text-white text-4xl group-hover:scale-110 transition-transform duration-300"></i>

                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-purple-400/50 to-purple-400/30 rounded-3xl opacity-0 group-hover:opacity-100 group-hover:scale-125 blur-xl transition-all duration-500"></div>
                    </div>

                    {/* Floating Ring */}
                    <div className="absolute inset-0 w-24 h-24 mx-auto border-2 border-purple-300/30 rounded-3xl group-hover:scale-150 group-hover:border-purple-400/20 transition-all duration-700 opacity-0 group-hover:opacity-100"></div>
                  </div>

                  {/* Content Container */}
                  <div className="relative bg-white/20 backdrop-blur-sm rounded-2xl p-6 border border-white/30 group-hover:bg-white/30 transition-all duration-500">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-purple-700 transition-colors duration-300">
                      Download Catalogs
                    </h3>
                    
                    <div className="w-12 h-1 bg-purple-500 rounded-full mx-auto mb-6 group-hover:w-20 transition-all duration-500 shadow-lg"></div>
                    
                    <p className="text-gray-700 mb-6 leading-relaxed group-hover:text-gray-800 transition-colors duration-300">
                      Access detailed product specifications, brochures, and technical documentation.
                    </p>
                    
                    <div className="inline-flex items-center px-4 py-2 bg-white/30 backdrop-blur-xl border border-white/40 rounded-xl text-purple-600 font-medium group-hover:bg-white/50 transition-all duration-300">
                      Get Catalogs
                    </div>

                    {/* Interactive Progress Bar */}
                    <div className="mt-6 h-2 bg-white/30 rounded-full overflow-hidden">
                      <div className="h-full bg-purple-500 rounded-full transform -translate-x-full group-hover:translate-x-0 transition-transform duration-1000 shadow-lg"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Service Request Form */}
          {activeForm === 'serviceRequest' && (
            <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl border border-white/30 relative overflow-hidden">
              {/* Glass Background Layers */}
              <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-blue-500/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mr-4 border border-white/30">
                      <i className="ri-tools-line text-blue-600 text-xl"></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">Service Request Form</h3>
                  </div>
                  <button
                    onClick={() => setActiveForm(null)}
                    className="p-3 hover:bg-white/20 backdrop-blur-xl rounded-full transition-all duration-300 border border-white/30 group cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-600 group-hover:scale-110 transition-transform duration-300"></i>
                  </button>
                </div>
                
                <form
                  id="serviceRequestForm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit('serviceRequest');
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.serviceRequest.name}
                      onChange={(e) => handleInputChange('serviceRequest', 'name', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.serviceRequest.email}
                      onChange={(e) => handleInputChange('serviceRequest', 'email', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.serviceRequest.phone}
                      onChange={(e) => handleInputChange('serviceRequest', 'phone', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.serviceRequest.company}
                      onChange={(e) => handleInputChange('serviceRequest', 'company', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Industry
                    </label>
                    <select
                      name="industry"
                      value={formData.serviceRequest.industry}
                      onChange={(e) => handleInputChange('serviceRequest', 'industry', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Industry</option>
                      {industries.map(industry => (
                        <option key={industry} value={industry}>{industry}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Service Type *
                    </label>
                    <select
                      name="serviceType"
                      required
                      value={formData.serviceRequest.serviceType}
                      onChange={(e) => handleInputChange('serviceRequest', 'serviceType', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Service Type</option>
                      {serviceTypes.map(service => (
                        <option key={service} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Equipment Details
                    </label>
                    <input
                      type="text"
                      name="equipment"
                      placeholder="Brand, model, capacity, etc."
                      value={formData.serviceRequest.equipment}
                      onChange={(e) => handleInputChange('serviceRequest', 'equipment', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-5
0 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Service Description
                    </label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      placeholder="Describe the service you need..."
                      value={formData.serviceRequest.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          handleInputChange('serviceRequest', 'message', e.target.value);
                        }
                      }}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none shadow-lg transition-all duration-300"
                    ></textarea>
                    <p className="text-xs text-gray-600 mt-2">
                      {formData.serviceRequest.message.length}/500 characters
                    </p>
                  </div>

                  {/* Turnstile CAPTCHA */}
                  <div className="md:col-span-2">
                    <div className="cf-turnstile" data-sitekey={TURNSTILE_SITE_KEY} data-theme="light"></div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="group/btn relative">
                      <div className="absolute inset-0 bg-blue-700/80 backdrop-blur-xl rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-500 shadow-2xl"></div>
                      <button type="submit" className="relative w-full bg-blue-600/90 backdrop-blur-2xl hover:bg-blue-700/90 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                        <span className="flex items-center justify-center">
                          <div className="w-6 h-6 bg-white/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 group-hover/btn:scale-110 transition-transform duration-300">
                            <i className="ri-send-plane-line text-sm"></i>
                          </div>
                          Submit Service Request
                        </span>
                      </button>
                    </div>
                    {submitStatus.serviceRequest && (
                      <p className={`text-center mt-4 text-sm font-medium ${
                        submitStatus.serviceRequest.includes('success')
                          ? 'text-green-600'
                          : submitStatus.serviceRequest.includes('Submitting')
                          ? 'text-blue-600'
                          : 'text-red-600'
                      }`}>
                        {submitStatus.serviceRequest}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Consultation Form */}
          {activeForm === 'consultation' && (
            <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl border border-white/30 relative overflow-hidden">
              {/* Glass Background Layers */}
              <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-teal-500/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mr-4 border border-white/30">
                      <i className="ri-calculator-line text-teal-600 text-xl"></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">Quote Request Form</h3>
                  </div>
                  <button
                    onClick={() => setActiveForm(null)}
                    className="p-3 hover:bg-white/20 backdrop-blur-xl rounded-full transition-all duration-300 border border-white/30 group cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-600 group-hover:scale-110 transition-transform duration-300"></i>
                  </button>
                </div>
                
                <form
                  id="consultationForm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit('consultation');
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.consultation.name}
                      onChange={(e) => handleInputChange('consultation', 'name', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.consultation.email}
                      onChange={(e) => handleInputChange('consultation', 'email', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.consultation.phone}
                      onChange={(e) => handleInputChange('consultation', 'phone', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.consultation.company}
                      onChange={(e) => handleInputChange('consultation', 'company', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Industry
                    </label>
                    <select
                      name="industry"
                      value={formData.consultation.industry}
                      onChange={(e) => handleInputChange('consultation', 'industry', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Industry</option>
                      {industries.map(industry => (
                        <option key={industry} value={industry}>{industry}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Equipment Type *
                    </label>
                    <select
                      name="equipmentType"
                      required
                      value={formData.consultation.equipmentType}
                      onChange={(e) => handleInputChange('consultation', 'equipmentType', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Equipment Type</option>
                      {equipmentTypes.map(equipment => (
                        <option key={equipment} value={equipment}>{equipment}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Capacity Required
                    </label>
                    <input
                      type="text"
                      name="capacity"
                      placeholder="e.g., 50kg, 100kg"
                      value={formData.consultation.capacity}
                      onChange={(e) => handleInputChange('consultation', 'capacity', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Budget Range
                    </label>
                    <select
                      name="budget"
                      value={formData.consultation.budget}
                      onChange={(e) => handleInputChange('consultation', 'budget', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Budget Range</option>
                      <option value="Under 10 Lakhs">Under ₹10 Lakhs</option>
                      <option value="10-25 Lakhs">₹10-25 Lakhs</option>
                      <option value="25-50 Lakhs">₹25-50 Lakhs</option>
                      <option value="50 Lakhs - 1 Crore">₹50 Lakhs - 1 Crore</option>
                      <option value="Above 1 Crore">Above ₹1 Crore</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Timeline
                    </label>
                    <select
                      name="timeline"
                      value={formData.consultation.timeline}
                      onChange={(e) => handleInputChange('consultation', 'timeline', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Timeline</option>
                      <option value="Immediate">Immediate (Within 1 month)</option>
                      <option value="1-3 months">1-3 months</option>
                      <option value="3-6 months">3-6 months</option>
                      <option value="6-12 months">6-12 months</option>
                      <option value="Future planning">Future planning</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Project Requirements
                    </label>
                    <textarea
                      name="requirements"
                      rows={4}
                      maxLength={500}
                      placeholder="Describe your project requirements, space constraints, equipment specifications..."
                      value={formData.consultation.requirements}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          handleInputChange('consultation', 'requirements', e.target.value);
                        }
                      }}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none shadow-lg transition-all duration-300"
                    ></textarea>
                    <p className="text-xs text-gray-600 mt-2">
                      {formData.consultation.requirements.length}/500 characters
                    </p>
                  </div>

                  {/* Turnstile CAPTCHA */}
                  <div className="md:col-span-2">
                    <div className="cf-turnstile" data-sitekey={TURNSTILE_SITE_KEY} data-theme="light"></div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="group/btn relative">
                      <div className="absolute inset-0 bg-teal-700/80 backdrop-blur-xl rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-500 shadow-2xl"></div>
                      <button type="submit" className="relative w-full bg-teal-600/90 backdrop-blur-2xl hover:bg-teal-700/90 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                        <span className="flex items-center justify-center">
                          <div className="w-6 h-6 bg-white/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 group-hover/btn:scale-110 transition-transform duration-300">
                            <i className="ri-calculator-check-line text-sm"></i>
                          </div>
                          Request Quote
                        </span>
                      </button>
                    </div>
                    {submitStatus.consultation && (
                      <p className={`text-center mt-4 text-sm font-medium ${
                        submitStatus.consultation.includes('success')
                          ? 'text-green-600'
                          : submitStatus.consultation.includes('Submitting')
                          ? 'text-blue-600'
                          : 'text-red-600'
                      }`}>
                        {submitStatus.consultation}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Catalog Download Form */}
          {activeForm === 'catalog' && (
            <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl border border-white/30 relative overflow-hidden">
              {/* Glass Background Layers */}
              <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-8">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-purple-500/20 backdrop-blur-xl rounded-2xl flex items-center justify-center mr-4 border border-white/30">
                      <i className="ri-download-line text-purple-600 text-xl"></i>
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">Download Product Catalogs</h3>
                  </div>
                  <button
                    onClick={() => setActiveForm(null)}
                    className="p-3 hover:bg-white/20 backdrop-blur-xl rounded-full transition-all duration-300 border border-white/30 group cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-600 group-hover:scale-110 transition-transform duration-300"></i>
                  </button>
                </div>
                
                <form
                  id="catalogForm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit('catalog');
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.catalog.name}
                      onChange={(e) => handleInputChange('catalog', 'name', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.catalog.email}
                      onChange={(e) => handleInputChange('catalog', 'email', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.catalog.phone}
                      onChange={(e) => handleInputChange('catalog', 'phone', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.catalog.company}
                      onChange={(e) => handleInputChange('catalog', 'company', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Your Position
                    </label>
                    <input
                      type="text"
                      name="position"
                      placeholder="e.g., Purchase Manager, Owner"
                      value={formData.catalog.position}
                      onChange={(e) => handleInputChange('catalog', 'position', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-5
0 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Industry
                    </label>
                    <select
                      name="industry"
                      value={formData.catalog.industry}
                      onChange={(e) => handleInputChange('catalog', 'industry', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Industry</option>
                      {industries.map(industry => (
                        <option key={industry} value={industry}>{industry}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Catalog Type *
                    </label>
                    <select
                      name="catalogType"
                      required
                      value={formData.catalog.catalogType}
                      onChange={(e) => handleInputChange('catalog', 'catalogType', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm pr-8 shadow-lg transition-all duration-300"
                    >
                      <option value="">Select Catalog Type</option>
                      {catalogTypes.map(catalog => (
                        <option key={catalog} value={catalog}>{catalog}</option>
                      ))}
                    </select>
                  </div>

                  {/* Turnstile CAPTCHA */}
                  <div className="md:col-span-2">
                    <div className="cf-turnstile" data-sitekey={TURNSTILE_SITE_KEY} data-theme="light"></div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="group/btn relative">
                      <div className="absolute inset-0 bg-purple-700/80 backdrop-blur-xl rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-500 shadow-2xl"></div>
                      <button type="submit" className="relative w-full bg-purple-600/90 backdrop-blur-2xl hover:bg-purple-700/90 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                        <span className="flex items-center justify-center">
                          <div className="w-6 h-6 bg-white/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 group-hover/btn:scale-110 transition-transform duration-300">
                            <i className="ri-download-cloud-line text-sm"></i>
                          </div>
                          Download Catalog
                        </span>
                      </button>
                    </div>
                    {submitStatus.catalog && (
                      <p className={`text-center mt-4 text-sm font-medium ${
                        submitStatus.catalog.includes('success')
                          ? 'text-green-600'
                          : submitStatus.catalog.includes('Submitting')
                          ? 'text-blue-600'
                          : 'text-red-600'
                      }`}>
                        {submitStatus.catalog}
                      </p>
                    )}
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* General Contact Form - New Addition */}
          {!activeForm && (
            <div className="mt-16 bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl border border-white/30 relative overflow-hidden">
              {/* Glass Background Layers */}
              <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
              
              {/* Floating Particles */}
              <div className="absolute top-4 right-4 w-1 h-1 bg-cyan-400/60 rounded-full animate-ping"></div>
              <div className="absolute bottom-6 left-6 w-1.5 h-1.5 bg-cyan-400/50 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>

              <div className="relative z-10">
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-3xl flex items-center justify-center shadow-2xl border border-white/30 relative">
                      {/* 3D Shadow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-3xl transform translate-x-2 translate-y-2 -z-10"></div>
                      <i className="ri-customer-service-2-line text-white text-2xl"></i>
                      
                      {/* Icon Glow Effect */}
                      <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/50 to-cyan-400/30 rounded-3xl opacity-75 scale-125 blur-xl"></div>
                    </div>
                  </div>
                  
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Contact Us</h3>
                  <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto">
                    Have a general inquiry or want to discuss your laundry equipment needs? Send us a message and our team will get back to you promptly.
                  </p>
                </div>
                
                <form
                  id="contactUsForm"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit('contactUs');
                  }}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.contactUs.name}
                      onChange={(e) => handleInputChange('contactUs', 'name', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.contactUs.email}
                      onChange={(e) => handleInputChange('contactUs', 'email', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.contactUs.phone}
                      onChange={(e) => handleInputChange('contactUs', 'phone', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.contactUs.company}
                      onChange={(e) => handleInputChange('contactUs', 'company', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Subject *
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      placeholder="Brief description of your inquiry"
                      value={formData.contactUs.subject}
                      onChange={(e) => handleInputChange('contactUs', 'subject', e.target.value)}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm shadow-lg transition-all duration-300"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Message *
                    </label>
                    <textarea
                      name="message"
                      rows={5}
                      maxLength={500}
                      required
                      placeholder="Tell us about your requirements, questions, or how we can help you..."
                      value={formData.contactUs.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          handleInputChange('contactUs', 'message', e.target.value);
                        }
                      }}
                      className="w-full px-4 py-3 bg-white/50 backdrop-blur-xl border border-white/40 rounded-xl focus:ring-2 focus:ring-cyan-500 focus:border-transparent text-sm resize-none shadow-lg transition-all duration-300"
                    ></textarea>
                    <p className="text-xs text-gray-600 mt-2">
                      {formData.contactUs.message.length}/500 characters
                    </p>
                  </div>

                  {/* Turnstile CAPTCHA */}
                  <div className="md:col-span-2">
                    <div className="cf-turnstile" data-sitekey={TURNSTILE_SITE_KEY} data-theme="light"></div>
                  </div>

                  <div className="md:col-span-2">
                    <div className="group/btn relative">
                      <div className="absolute inset-0 bg-cyan-700/80 backdrop-blur-xl rounded-2xl transform translate-x-2 translate-y-2 group-hover/btn:translate-x-3 group-hover/btn:translate-y-3 transition-transform duration-500 shadow-2xl"></div>
                      <button type="submit" className="relative w-full bg-cyan-600/90 backdrop-blur-2xl hover:bg-cyan-700/90 text-white font-bold px-8 py-4 rounded-2xl transition-all duration-500 transform hover:-translate-y-1 hover:scale-105 shadow-2xl hover:shadow-3xl whitespace-nowrap border-2 border-white/20">
                        <span className="flex items-center justify-center">
                          <div className="w-6 h-6 bg-white/20 backdrop-blur-xl rounded-lg flex items-center justify-center mr-3 group-hover/btn:scale-110 transition-transform duration-300">
                            <i className="ri-send-plane-2-line text-sm"></i>
                          </div>
                          Send Message
                        </span>
                      </button>
                    </div>
                    {submitStatus.contactUs && (
                      <p className={`text-center mt-4 text-sm font-medium ${
                        submitStatus.contactUs.includes('success')
                          ? 'text-green-600'
                          : submitStatus.contactUs.includes('Submitting')
                          ? 'text-blue-600'
                          : 'text-red-600'
                      }`}>
                        {submitStatus.contactUs}
                      </p>
                    )}
                  </div>
                </form>
              </div>

              {/* Subtle Shine Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full hover:translate-x-full transition-transform duration-1000 rounded-3xl pointer-events-none"></div>
            </div>
          )}

        </div>
      </section>

      {/* Map Section - Professional Glass Design */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50 relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/20 rounded-full blur-2xl animate-pulse"></div>
          <div className="absolute bottom-20 right-20 w-28 h-28 bg-teal-200/25 rounded-full blur-xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/60 backdrop-blur-2xl border border-white/30 text-blue-800 text-sm font-semibold shadow-2xl mb-6">
              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-3">
                <i className="ri-map-pin-line text-blue-600 text-sm"></i>
              </div>
              Visit Our Facility
              <div className="w-2 h-2 bg-blue-500 rounded-full ml-3 animate-pulse"></div>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Visit Our Location
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Come see our showroom and meet our expert team in person
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-8 shadow-2xl border border-white/30 relative overflow-hidden">
              {/* Glass Background Layers */}
              <div className="absolute inset-0 bg-white/20 backdrop-blur-xl rounded-3xl"></div>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-white/5 rounded-3xl"></div>
              
              <div className="relative z-10">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">Our Head Office</h3>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-blue-500/20 backdrop-blur-xl rounded-2xl flex items-center justify-center border border-white/30">
                      <i className="ri-map-pin-line text-xl text-blue-600"></i>
                    </div>
                    <div>
                      <p className="text-gray-700">Sy No. 53/4, Site No. 5, P &amp; T Layout Main Road</p>
                      <p className="text-gray-700">Ashwath Nagar, Srigadhakavalu</p>
                      <p className="text-gray-700">Sunkadakatte, Bangalore - 560091</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-teal-500/20 backdrop-blur-xl rounded-2xl flex items-center justify-center border border-white/30">
                      <i className="ri-time-line text-xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 mb-2">Business Hours</p>
                      <p className="text-gray-700">Monday - Friday: 9:00 AM - 6:00 PM</p>
                      <p className="text-gray-700">Saturday: 9:00 AM - 2:00 PM</p>
                      <p className="text-gray-700">Sunday: Closed</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-purple-500/20 backdrop-blur-xl rounded-2xl flex items-center justify-center border border-white/30">
                      <i className="ri-phone-line text-xl text-purple-600"></i>
                    </div>
                    <div>
                      <p className="font-bold text-gray-900 mb-2">Contact</p>
                      <p className="text-gray-700">+91 99808 72453</p>
                      <p className="text-gray-700">info@galaxyfabtech.com</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/40 backdrop-blur-2xl rounded-3xl p-4 shadow-2xl border border-white/30">
                <div className="h-96 rounded-2xl overflow-hidden shadow-xl">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3508.5123456789!2d77.0266!3d28.4089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDI0JzMyLjAiTiA3N8KwMDEnMzUuOCJF!5e0!3m2!1sen!2sin!4v1234567890"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="GalaxyFabTech Location"
                  ></iframe>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Contact Bar - Enhanced Glass Design */}
      <section className="py-12 bg-gradient-to-r from-blue-600/90 to-teal-600/90 backdrop-blur-2xl relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700/20 to-teal-700/20"></div>
        <div className="absolute top-0 left-1/4 w-24 h-24 bg-white/10 rounded-full blur-2xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-32 h-32 bg-white/5 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between text-white">
            <div className="mb-6 md:mb-0 text-center md:text-left">
              <h3 className="text-3xl font-bold mb-3">Ready to Get Started?</h3>
              <p className="text-blue-100 text-lg">Contact our experts today for personalized laundry equipment solutions</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="group/btn relative">
                <div className="absolute inset-0 bg-white/30 backdrop-blur-xl rounded-2xl transform translate-x-2 translate-y-2 group-hover:btn:translate-x-3 group-hover:btn:translate-y-3 transition-transform duration-500 shadow-2xl"></div>
                <a href="tel:+919980872453" className="relative flex items-center space-x-3 bg-white/20 backdrop-blur-2xl px-8 py-4 rounded-2xl hover:bg-white/30 transition-all duration-500 cursor-pointer border border-white/30 group-hover:btn:scale-105 shadow-2xl">
                  <div className="w-6 h-6 bg-white/20 backdrop-blur-xl rounded-lg flex items-center justify-center group-hover:btn:scale-110 transition-transform duration-300">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <span className="font-bold text-lg">Call Now</span>
                </a>
              </div>
              <div className="group/btn relative">
                <div className="absolute inset-0 bg-white/30 backdrop-blur-xl rounded-2xl transform translate-x-2 translate-y-2 group-hover:btn:translate-x-3 group-hover:btn:translate-y-3 transition-transform duration-500 shadow-2xl"></div>
                <a href="mailto:info@galaxyfabtech.com" className="relative flex items-center space-x-3 bg-white/20 backdrop-blur-2xl px-8 py-4 rounded-2xl hover:bg-white/30 transition-all duration-500 cursor-pointer border border-white/30 group-hover:btn:scale-105 shadow-2xl">
                  <div className="w-6 h-6 bg-white/20 backdrop-blur-xl rounded-lg flex items-center justify-center group-hover:btn:scale-110 transition-transform duration-300">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <span className="font-bold text-lg">Email Us</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
